import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/modules/shared/ui/components/sheet"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/modules/shared/ui/components/tabs"
import { Separator } from "@/modules/shared/ui/components/separator"
import { AssignmentsTable } from "./AssignmentsTable"
import { AssignAccessForm } from "./AssignAccessForm"
import { AssignmentAuditLog } from "./AssignmentAuditLog"
import {
  useAssignResource,
  useResourceAssignments,
  useRevokeResource,
} from "../../hooks/useResourceAssignments"
import { AssignResourceAccessRequest, ResourceType } from "../../dtos/ResourceAccessDtos"

interface AccessPanelProps {
  open: boolean
  onClose: () => void
  type: ResourceType
  resourceId: string
  resourceName: string
}

function AccessPanelContent({ type, resourceId }: { type: ResourceType; resourceId: string }) {
  const { data: assignments = [], isLoading } = useResourceAssignments(type, resourceId)
  const assignMutation = useAssignResource(type, resourceId)
  const revokeMutation = useRevokeResource(type, resourceId)

  const handleAssign = (userId: string, request: AssignResourceAccessRequest) => {
    assignMutation.mutate({ userId, request })
  }

  const userNames = new Map(assignments.map((a) => [a.userId, a.userName]))

  return (
    <Tabs defaultValue="access">
      <TabsList className="w-full">
        <TabsTrigger value="access">Access</TabsTrigger>
        <TabsTrigger value="history">History</TabsTrigger>
      </TabsList>

      <TabsContent value="access" className="space-y-6 mt-4">
        <div>
          <h3 className="text-sm font-semibold mb-3">Who has access</h3>
          <AssignmentsTable
            assignments={assignments}
            isLoading={isLoading}
            onRevoke={(userId) => revokeMutation.mutate(userId)}
            isRevoking={revokeMutation.isPending}
          />
        </div>

        <Separator />

        <div>
          <h3 className="text-sm font-semibold mb-3">Assign access</h3>
          <AssignAccessForm
            onAssign={handleAssign}
            isPending={assignMutation.isPending}
            alreadyAssignedUserIds={assignments.map((a) => a.userId)}
          />
        </div>
      </TabsContent>

      <TabsContent value="history" className="mt-4">
        <AssignmentAuditLog type={type} resourceId={resourceId} userNames={userNames} />
      </TabsContent>
    </Tabs>
  )
}

/**
 * Manage which team members can see/edit a Vacancy, Candidate or Employee. With no assignments the
 * record stays visible to the whole organization; assigning anyone restricts it to the owner plus
 * assignees. Owner-only — the caller must already be gated to OrganizationOwner before opening this.
 */
export function AccessPanel({ open, onClose, type, resourceId, resourceName }: AccessPanelProps) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-xl">
        <SheetHeader className="border-b pr-12">
          <SheetTitle>Manage access</SheetTitle>
          <SheetDescription className="truncate">{resourceName}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4">
          {open && resourceId && <AccessPanelContent type={type} resourceId={resourceId} />}
        </div>
      </SheetContent>
    </Sheet>
  )
}
